'use client';

import { KeyRound, Mail } from 'lucide-react';

import { Button } from '@kit/ui/button';
import { Trans } from '@kit/ui/trans';

export type AuthMethod = 'password' | 'phone' | 'otp' | 'magicLink';

// Priority order for the DEFAULT method on both sign-in and sign-up, shared
// by the web methods containers AND the native auth pages: email & password
// is the primary flow when enabled; phone (WhatsApp OTP) and email-code are
// offered as alternatives below it. Returning users land on their last-used
// method via useLastAuthMethodPreference regardless of this order.
export const AUTH_METHOD_PRIORITY: AuthMethod[] = [
  'password',
  'phone',
  'otp',
  'magicLink',
];

/**
 * One "Continue with …" switcher button per non-active auth method. Shared by
 * SignInMethodsContainer and SignUpMethodsContainer so the alternative-methods
 * section looks and behaves identically on both pages.
 *
 * Phone OTPs are delivered over WhatsApp (Send-SMS hook → send-email-whatsapp
 * webhook), so the phone method is branded as WhatsApp with its official green.
 */
export function AuthMethodButton({
  method,
  onSelect,
}: {
  method: AuthMethod;
  onSelect: () => void;
}) {
  if (method === 'phone') {
    return (
      <Button
        type="button"
        className="w-full bg-[#25D366] text-white hover:bg-[#20BD5A]"
        onClick={onSelect}
        data-test="auth-method-whatsapp"
      >
        <WhatsAppIcon className="mr-2 h-4 w-4" />
        <Trans
          i18nKey="auth:continueWithWhatsApp"
          defaults="Continue with WhatsApp"
        />
      </Button>
    );
  }

  if (method === 'otp') {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={onSelect}
        data-test="auth-method-email-otp"
      >
        <Mail className="mr-2 h-4 w-4" />
        <Trans
          i18nKey="auth:continueWithEmailOtp"
          defaults="Continue with email code"
        />
      </Button>
    );
  }

  if (method === 'password') {
    return (
      <Button
        type="button"
        variant="outline"
        className="w-full"
        onClick={onSelect}
        data-test="auth-method-password"
      >
        <KeyRound className="mr-2 h-4 w-4" />
        <Trans
          i18nKey="auth:continueWithPassword"
          defaults="Continue with email & password"
        />
      </Button>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      className="w-full"
      onClick={onSelect}
      data-test="auth-method-magic-link"
    >
      <Mail className="mr-2 h-4 w-4" />
      <Trans
        i18nKey="auth:continueWithMagicLink"
        defaults="Continue with magic link"
      />
    </Button>
  );
}

// Compact header above the ACTIVE method's form. The password and email-code
// identify forms are visually identical (one email field + Continue), so
// without this the user can't tell which flow they're in or what happens
// after Continue. Render it only when the page offers more than one method.
export function AuthMethodHeader({ method }: { method: AuthMethod }) {
  const config =
    method === 'phone'
      ? {
          icon: <WhatsAppIcon className="h-4 w-4" />,
          iconClass: 'bg-[#25D366]/15 text-[#1FAF54]',
          titleKey: 'auth:methodHeaderPhoneTitle',
          titleDefault: 'WhatsApp',
          descriptionKey: 'auth:methodHeaderPhoneDescription',
          descriptionDefault: "We'll send a one-time code to your WhatsApp.",
        }
      : method === 'otp'
        ? {
            icon: <Mail className="h-4 w-4" />,
            iconClass: 'bg-primary/10 text-primary',
            titleKey: 'auth:methodHeaderOtpTitle',
            titleDefault: 'Email verification code',
            descriptionKey: 'auth:methodHeaderOtpDescription',
            descriptionDefault:
              "No password needed — we'll email you a one-time code.",
          }
        : method === 'password'
          ? {
              icon: <KeyRound className="h-4 w-4" />,
              iconClass: 'bg-primary/10 text-primary',
              titleKey: 'auth:methodHeaderPasswordTitle',
              titleDefault: 'Email & password',
              descriptionKey: 'auth:methodHeaderPasswordDescription',
              descriptionDefault: 'Use your email address and password.',
            }
          : {
              icon: <Mail className="h-4 w-4" />,
              iconClass: 'bg-primary/10 text-primary',
              titleKey: 'auth:methodHeaderMagicLinkTitle',
              titleDefault: 'Magic link',
              descriptionKey: 'auth:methodHeaderMagicLinkDescription',
              descriptionDefault: "We'll email you a link that signs you in.",
            };

  return (
    <div
      className="bg-muted/40 flex items-center gap-3 rounded-lg border px-3 py-2.5"
      data-test={`auth-method-header-${method}`}
    >
      <div
        className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${config.iconClass}`}
      >
        {config.icon}
      </div>

      <div className="min-w-0">
        <p className="text-foreground text-sm font-medium">
          <Trans i18nKey={config.titleKey} defaults={config.titleDefault} />
        </p>

        <p className="text-muted-foreground text-xs">
          <Trans
            i18nKey={config.descriptionKey}
            defaults={config.descriptionDefault}
          />
        </p>
      </div>
    </div>
  );
}

export function WhatsAppIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      className={className}
    >
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
    </svg>
  );
}
